import type { DataType } from '@glueapp/graph-types';
import { unwrapOptional } from '@glueapp/graph-types';

/**
 * A port is eligible for a user-defined custom type when its declared type
 * has "slack" — a generic, an open object, or an array whose element is generic.
 * For anything already concrete, there is nothing for the user to pick.
 */
export function canHaveUserDefinedType(portType: DataType): boolean {
    const inner = unwrapOptional(portType);
    switch (inner.kind) {
        case 'generic':
            return true;
        case 'object':
            return inner.restriction === 'open';
        case 'array':
            return inner.subType.kind === 'generic';
        default:
            return false;
    }
}

/**
 * Extracts the restriction that the editor should use when the user picks a
 * custom type. Generics with `extends` constrain the editor to subtypes of
 * that extends; unrestricted generics (and other eligible shapes) have no
 * restriction — the user can pick anything.
 */
export function getEditorRestrictionForPort(portType: DataType): DataType | undefined {
    const inner = unwrapOptional(portType);
    if (inner.kind === 'generic' && inner.extends) {
        return inner.extends;
    }
    return undefined;
}

/**
 * Picks a sensible default starting type when the user first opens the editor
 * and no value has been chosen yet.
 */
export function getInitialUserDefinedType(portType: DataType): DataType {
    const inner = unwrapOptional(portType);
    if (inner.kind === 'object') {
        return { kind: 'object', restriction: 'open', properties: {} };
    }
    if (inner.kind === 'array') {
        return { kind: 'array', subType: { kind: 'string' } };
    }
    return { kind: 'string' };
}
